import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getMetrics, getUserById, type MetricsData } from '../../services/admin';
import { useToast } from '../../hooks/useToast';

export default function AdminMetrics() {
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [topCoaches, setTopCoaches] = useState<Array<{ id: string; name: string; count: number }>>([]);
  const [topPlayers, setTopPlayers] = useState<Array<{ id: string; name: string; count: number }>>([]);
  const { showError } = useToast();

  useEffect(() => {
    loadMetrics();
  }, []);

  async function loadMetrics() {
    try {
      setLoading(true);
      const data = await getMetrics();
      setMetrics(data);

      // Get top coaches
      const coachEntries = Object.entries(data.lessonsPerCoach)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10);

      const coachesWithNames = await Promise.all(
        coachEntries.map(async ([id, count]) => {
          const user = await getUserById(id);
          return {
            id,
            name: user?.displayName || user?.username || 'Unknown Coach',
            count,
          };
        })
      );
      setTopCoaches(coachesWithNames);

      // Get top players
      const playerEntries = Object.entries(data.lessonsPerPlayer)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10);

      const playersWithNames = await Promise.all(
        playerEntries.map(async ([id, count]) => {
          const user = await getUserById(id);
          return {
            id,
            name: user?.displayName || user?.username || 'Unknown Player',
            count,
          };
        })
      );
      setTopPlayers(playersWithNames);
    } catch (error) {
      console.error('Error loading metrics:', error);
      showError('Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="p-8">
        <div className="text-center text-gray-500">Failed to load metrics</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">MVP Metrics</h1>
        <p className="text-gray-600">Basic platform statistics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Total Users</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalUsers}</p>
            </div>
            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
          </div>
          <div className="mt-4 flex gap-4 text-sm">
            <div>
              <span className="text-gray-600">Coaches: </span>
              <span className="font-semibold text-blue-600">{metrics.totalCoaches}</span>
            </div>
            <div>
              <span className="text-gray-600">Players: </span>
              <span className="font-semibold text-green-600">{metrics.totalPlayers}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Completed Lessons</p>
              <p className="text-3xl font-bold text-gray-900">{metrics.totalLessons}</p>
            </div>
            <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
          <Link
            to="/admin/lessons"
            className="mt-4 text-sm text-tennis-green hover:text-tennis-ball no-underline"
          >
            View all lessons →
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Active Coaches</p>
              <p className="text-3xl font-bold text-gray-900">
                {Object.keys(metrics.lessonsPerCoach).length}
              </p>
            </div>
            <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-6 h-6 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
          <p className="mt-4 text-sm text-gray-600">
            Coaches with at least 1 completed lesson
          </p>
        </div>
      </div>

      {/* Top Coaches and Players */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Top Coaches */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Top Coaches by Lessons</h3>
          </div>
          <div className="p-6">
            {topCoaches.length > 0 ? (
              <div className="space-y-4">
                {topCoaches.map((coach, index) => (
                  <div key={coach.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-blue-600">
                          {index + 1}
                        </span>
                      </div>
                      <Link
                        to={`/coach/${coach.id}`}
                        className="text-sm font-medium text-tennis-green hover:text-tennis-ball no-underline"
                      >
                        {coach.name}
                      </Link>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {coach.count} lessons
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No coach data available
              </p>
            )}
          </div>
        </div>

        {/* Top Players */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Top Players by Lessons</h3>
          </div>
          <div className="p-6">
            {topPlayers.length > 0 ? (
              <div className="space-y-4">
                {topPlayers.map((player, index) => (
                  <div key={player.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                        <span className="text-sm font-semibold text-green-600">
                          {index + 1}
                        </span>
                      </div>
                      <Link
                        to={`/player/${player.id}`}
                        className="text-sm font-medium text-tennis-green hover:text-tennis-ball no-underline"
                      >
                        {player.name}
                      </Link>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">
                      {player.count} lessons
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                No player data available
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
